import { FC, memo, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import { AnimatePresence } from "framer-motion";

import Button from "@/components/Button";
import Text from "@/components/Text";
import ToggableHeading from "@/components/ToggableHeading";
import ProjectModal from "@/pages/PreProduction/components/ProjectModal";
import { ProductionProjectResource } from "@/types/resources";

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 24px;
`;

const InfoBox = styled(Box)`
  margin-bottom: 24px;
  display: flex;
  position: relative;
`;

const Heading = styled(Text)`
  margin-bottom: 6px;
`;

const StyledText = styled(Text)`
  line-height: 22px;
`;

const Holder = styled.div`
  margin-bottom: 60px;
`;

const Column = styled.div`
  flex: 1;
  display: grid;
  grid-gap: 22px;
  align-content: start;
  padding-right: 24px;
`;

const ColumnRight = styled(Column)`
  border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding-right: 0;
  padding-left: 24px;
`;

const InfoNotes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoNote = styled(Text)`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 50px;
  padding: 4px 16px;
`;

const EditButton = styled(Button)`
  position: absolute;
  top: 24px;
  right: 24px;
  height: 38px;
  font-size: 14px;
  padding: 0 30px;
`;

interface Props {
  project: ProductionProjectResource;
}

const MainInfo: FC<Props> = memo(({ project }) => {
  const [isOpen, setIsOpen] = useState(true);

  const [showProjectModal, setShowProjectModal] = useState(false);

  return (
    <>
      <ToggableHeading
        heading="MAIN INFO"
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      {isOpen && (
        <Holder>
          <InfoBox>
            <Column>
              <EditButton
                color="secondary"
                onClick={() => setShowProjectModal(true)}
              >
                Edit
              </EditButton>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Project name
                </Heading>
                <StyledText $type="captionSemibold">{project.name}</StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Project owner
                </Heading>
                <StyledText $type="captionSemibold">{project.owner}</StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Channel
                </Heading>
                <StyledText $type="captionSemibold">
                  {project.channel.name}
                </StyledText>
              </div>
            </Column>
            <ColumnRight>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Date started
                </Heading>
                <StyledText $type="captionSemibold">
                  {DateTime.fromISO(project.createdAt).toLocaleString()}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Director
                </Heading>
                <StyledText $type="captionSemibold">
                  {project.director ?? "-"}
                </StyledText>
              </div>
              <div>
                <Heading $type="smallHeadline" $color="gray">
                  Project ID
                </Heading>
                <StyledText $type="captionSemibold">
                  {project.externalId}
                </StyledText>
              </div>
              {!!project.topics?.length && (
                <div>
                  <Heading $type="smallHeadline" $color="gray">
                    Topics
                  </Heading>
                  <InfoNotes>
                    {project.topics.map((topic) => (
                      <InfoNote key={topic.id} $type="captionSemibold">
                        {topic.title}
                      </InfoNote>
                    ))}
                  </InfoNotes>
                </div>
              )}
            </ColumnRight>
          </InfoBox>
          {project.inspiration && (
            <Box>
              <Heading $type="smallHeadline" $color="gray">
                Inspiration
              </Heading>
              <StyledText $type="captionSemibold">
                {project.inspiration}
              </StyledText>
            </Box>
          )}
        </Holder>
      )}
      <AnimatePresence>
        {showProjectModal && (
          <ProjectModal
            project={project}
            onClose={() => setShowProjectModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
});

export default MainInfo;
